import React from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { 
  Home, Users, Heart, Hospital, Brain, Package, ClipboardList, Stethoscope,
  ArrowLeft, Phone, Mail, Calendar, FileText, CheckCircle
} from 'lucide-react';

const serviceConfig = {
  'home-health': {
    title: 'Home Health',
    icon: Home,
    color: 'bg-green-500',
    description: 'Comprehensive skilled nursing and therapy services delivered in the comfort of your home.',
    features: [
      'Skilled Nursing Care',
      'Physical Therapy',
      'Occupational Therapy',
      'Speech Therapy',
      'Medical Social Services',
      'Home Health Aide Services'
    ],
    quickLinks: [
      { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
      { name: 'SOC/ROC Assessment', href: '/dashboard/forms/soc-roc' },
      { name: '485 Plan of Care', href: '/dashboard/clinical/poc-485' },
      { name: 'OASIS Assessment', href: '/dashboard/forms/oasis' },
    ]
  },
  'home-care': {
    title: 'Home Care',
    icon: Users,
    color: 'bg-purple-500',
    description: 'Non-medical personal care and assistance with daily living activities.',
    features: [
      'Personal Care Assistance',
      'Companionship Services',
      'Light Housekeeping',
      'Meal Preparation',
      'Transportation Assistance',
      'Medication Reminders'
    ],
    quickLinks: [
      { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
      { name: 'Care Plan', href: '/dashboard/clinical/poc-plus' },
      { name: 'Scheduling', href: '/dashboard/scheduling' },
    ]
  },
  'hospice': {
    title: 'Hospice',
    icon: Heart,
    color: 'bg-blue-500',
    description: 'Compassionate end-of-life care focused on comfort and dignity.',
    features: [
      'Pain & Symptom Management',
      'Emotional Support',
      'Spiritual Care',
      'Family Support & Counseling',
      'Bereavement Services',
      '24/7 On-Call Support'
    ],
    quickLinks: [
      { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
      { name: 'Clinical Forms', href: '/dashboard/forms/oasis' },
      { name: 'Care Plan', href: '/dashboard/clinical/poc-plus' },
    ]
  },
  'clinic': {
    title: 'Clinic',
    icon: Hospital,
    color: 'bg-teal-500',
    description: 'Outpatient clinical services and medical consultations.',
    features: [
      'Primary Care Visits',
      'Chronic Disease Management',
      'Preventive Care',
      'Lab Services',
      'Diagnostic Testing',
      'Specialist Referrals'
    ],
    quickLinks: [
      { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
      { name: 'Scheduling', href: '/dashboard/scheduling' },
      { name: 'Billing', href: '/dashboard/billing' },
    ]
  },
  'behavioral-health': {
    title: 'Behavioral Health',
    icon: Brain,
    color: 'bg-orange-500',
    description: 'Mental health and substance abuse treatment services.',
    features: [
      'Psychiatric Evaluation',
      'Individual Therapy',
      'Group Therapy',
      'Medication Management',
      'Crisis Intervention',
      'Substance Abuse Treatment'
    ],
    quickLinks: [
      { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
      { name: 'Clinical Forms', href: '/dashboard/forms/oasis' },
      { name: 'Communication Log', href: '/dashboard/patients/communication' },
    ]
  },
  'dme': {
    title: 'DME (Durable Medical Equipment)',
    icon: Package,
    color: 'bg-yellow-500',
    description: 'Medical equipment and supplies for home use.',
    features: [
      'Hospital Beds',
      'Wheelchairs & Mobility Aids',
      'Oxygen Equipment',
      'CPAP/BiPAP Machines',
      'Wound Care Supplies',
      'Diabetic Supplies'
    ],
    quickLinks: [
      { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
      { name: 'Orders', href: '/dashboard/clinical/orders' },
      { name: 'Billing', href: '/dashboard/billing' },
    ]
  },
  'case-management': {
    title: 'Case Management',
    icon: ClipboardList,
    color: 'bg-red-500',
    description: 'Coordinated care management and patient advocacy services.',
    features: [
      'Care Coordination',
      'Transition Planning',
      'Resource Coordination',
      'Patient Advocacy',
      'Insurance Navigation',
      'Community Resource Linkage'
    ],
    quickLinks: [
      { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
      { name: 'Communication Log', href: '/dashboard/patients/communication' },
      { name: 'Care Plan', href: '/dashboard/clinical/poc-plus' },
    ]
  },
  'concierge': {
    title: 'Concierge Visiting Doctors',
    icon: Stethoscope,
    color: 'bg-purple-600',
    description: 'Premium in-home physician visits and medical consultations.',
    features: [
      'In-Home Physician Visits',
      'Comprehensive Health Assessments',
      'Chronic Disease Management',
      'Medication Review',
      'Care Coordination',
      'After-Hours Availability'
    ],
    quickLinks: [
      { name: 'Patient Demographics', href: '/dashboard/patients/demographics' },
      { name: 'Scheduling', href: '/dashboard/scheduling' },
      { name: 'Video Call', href: '/dashboard/clinical/webrtc-test' },
    ]
  }
};

const ServicePage = () => {
  const { serviceType } = useParams();
  const service = serviceConfig[serviceType] || serviceConfig['home-health'];
  const Icon = service.icon;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid={`service-page-${serviceType}`}>
        {/* Back Button */}
        <Link 
          to="/dashboard/quick-links" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Quick Links
        </Link>

        {/* Header */}
        <div className={`${service.color} rounded-xl p-8 mb-8 text-white`}>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Icon className="w-8 h-8" />
            </div>
            <h1 className="text-3xl font-bold">{service.title}</h1>
          </div>
          <p className="text-white/90 text-lg max-w-2xl">{service.description}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Services Offered */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Services Offered</h2>
            <ul className="space-y-3">
              {service.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              {service.quickLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <FileText className="w-5 h-5 text-orange-500" />
                  <span className="text-gray-700 font-medium">{link.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-8 bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 text-white">
          <h2 className="text-xl font-bold mb-4">Need Assistance?</h2>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-orange-400" />
              <span>(562) 349-0860</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-400" />
              <span>info@ivory_hhc.com</span>
            </div>
            <Link 
              to="/dashboard/scheduling"
              className="flex items-center gap-2 bg-orange-500 px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Calendar className="w-5 h-5" />
              Schedule Appointment
            </Link>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ServicePage;
